import React, { createContext, useContext, useState, useCallback } from "react";

/**
 * Context used only by the "Category menu styling" prototype.
 * When provided, RetailerGlobalNavLoggedIn can apply prototype-specific styles to the
 * category menu (e.g. first column font size, borders) without affecting other prototypes.
 */
export type FirstColumnFontSize = "large" | "small";

type CategoryMenuStylingContextValue = {
  firstColumnFontSize: FirstColumnFontSize;
  setFirstColumnFontSize: (size: FirstColumnFontSize) => void;
  showColumnBorders: boolean;
  setShowColumnBorders: (show: boolean) => void;
};

const CategoryMenuStylingContext =
  createContext<CategoryMenuStylingContextValue | null>(null);

export function CategoryMenuStylingProvider({
  children,
  initialFirstColumnFontSize = "large",
  initialShowColumnBorders = false,
}: {
  children: React.ReactNode;
  initialFirstColumnFontSize?: FirstColumnFontSize;
  initialShowColumnBorders?: boolean;
}) {
  const [firstColumnFontSize, setFirstColumnFontSizeState] =
    useState<FirstColumnFontSize>(initialFirstColumnFontSize);
  const [showColumnBorders, setShowColumnBordersState] = useState(
    initialShowColumnBorders
  );
  const setFirstColumnFontSize = useCallback(
    (size: FirstColumnFontSize) => setFirstColumnFontSizeState(size),
    []
  );
  const setShowColumnBorders = useCallback(
    (show: boolean) => setShowColumnBordersState(show),
    []
  );
  const value: CategoryMenuStylingContextValue = {
    firstColumnFontSize,
    setFirstColumnFontSize,
    showColumnBorders,
    setShowColumnBorders,
  };
  return (
    <CategoryMenuStylingContext.Provider value={value}>
      {children}
    </CategoryMenuStylingContext.Provider>
  );
}

export function useCategoryMenuStyling(): CategoryMenuStylingContextValue | null {
  return useContext(CategoryMenuStylingContext);
}
